import { RoomType, UserRole, type Floor, type Gateway, type Room, type Valve } from "@eisox/api/core-api";
import dayjs, { type ConfigType } from "@eisox/dayjs";
import type { BORDER_ROUTER_STATUS } from "@eisox/gateways";
import {
  getBatteryThreshold,
  getValveProblem,
  VALVE_HARDWARE_VERSION_ENUM,
  VALVE_WARNINGS_ENUM,
  valveVersionHandler,
  type IValve,
} from "@eisox/valves";

import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

import { calculateInterpolatedCorrection } from "./correction.helper";
import { AvailableRoomTypes } from "./room.helper";

export const ONLY_INSTALLER_WARNINGS = [
  VALVE_WARNINGS_ENUM.DISCONNECTED_WARNING,
  VALVE_WARNINGS_ENUM.BAD_RSSI,
  VALVE_WARNINGS_ENUM.BAD_LQI,
  VALVE_WARNINGS_ENUM.LOW_BATTERY,
];

const TRIG_CUR_MA_FACTOR = 10;

export const formatTrigCurThreshold = (trigCur: number | undefined): string =>
  trigCur === undefined ? "-" : `${trigCur * TRIG_CUR_MA_FACTOR} mA`;

export const transformClosing = (closing: number, role: UserRole): number =>
  role === UserRole.INSTALLER ? closing : Math.min(Math.max(closing, 0), 100);

export const valvesToEnhancedValves = (
  valves: Valve[],
  floors: Floor[],
  gateways: Gateway[],
  rooms: Room[],
  date: ConfigType,
  role: UserRole,
  permissions: Permission,
  canSeeInstallerWarnings: boolean,
): EnhancedValve[] => {
  const getRoomType = (roomType: RoomType): (typeof AvailableRoomTypes)[number] =>
    (AvailableRoomTypes as readonly RoomType[]).includes(roomType)
      ? (roomType as (typeof AvailableRoomTypes)[number])
      : RoomType.ROOM;

  return valves.map(v => {
    let floor: Floor | undefined;
    let room: Room | undefined;
    let gateway: Gateway | undefined;

    if (permissions.floor?.read) {
      floor = floors.find(f => f.id === v.floor.id);
    }
    if (!floor) throw new Error(`Floor ${v.floor.id} not found for valve ${v.id}`);

    if (permissions.room?.read) {
      room = rooms.find(r => r.id === v.roomId);
    }
    if (!room) throw new Error(`Room ${v.roomId} not found for valve ${v.id}`);

    if (permissions.gateway?.read) {
      gateway = gateways.find(g => g.id === v.gatewayId);
    }

    const problem = getValveProblem(
      v as unknown as IValve,
      gateway
        ? { ...gateway, borderRouterStatus: [gateway.borderRouterStatus as unknown as BORDER_ROUTER_STATUS] }
        : undefined,
      date,
    );
    const errors = problem.errors;
    const warningsRaw = problem.warnings;
    const warnings = canSeeInstallerWarnings
      ? warningsRaw
      : warningsRaw.filter(w => !ONLY_INSTALLER_WARNINGS.includes(w as VALVE_WARNINGS_ENUM));
    const problemStatus =
      (problem.problemStatus === "error" && permissions.valve?.error) ||
      (problem.problemStatus === "warning" && warnings.length > 0 && permissions.valve?.warning)
        ? problem.problemStatus
        : undefined;

    return {
      ...v,
      // Use optional chaining or fallbacks if no permission
      floor: { x: v.floor.x, y: v.floor.y, id: floor.id, name: floor.name },
      roomName: room.name,
      roomType: getRoomType(room.type),
      gatewayName: gateway?.name ?? "-",
      gatewaySoftwareVersion: gateway?.softwareVersion ?? "-",
      gatewayMac: gateway?.mac ?? "-",
      errors,
      warnings,
      problemStatus,
      closing: v.closing !== undefined ? transformClosing(v.closing, role) : v.closing,
      correction: problemStatus !== "error" && v.instructionTemp !== v.correctedTemp ? v.correction : undefined,
    };
  });
};

/**
 * The function `getValvesByRoomId` filters an array of valves based on a given room ID.
 * @param {string} roomId - A string representing the ID of a room.
 * @param {ValvesMessageValvesInner[]} valves
 * @returns an array of valves that have a matching roomId value to the provided roomId parameter.
 */
export const getValvesByRoomId = <T extends { roomId: string }>(roomId: string, valves: T[]): T[] => {
  return valves.filter(valve => valve.roomId === roomId);
};

/**
 * Calculate the estimated temperature of a valve.
 * @param absenceTemperature The absence temperature.
 * @param confortTemperature The confort temperature.
 * @param temperature The temperature.
 * @param correction The correction.
 * @returns The estimated temperature.
 */
export const calculateEstimatedTemperature = (
  absenceTemperature: number,
  confortTemperature: number,
  temperature?: number,
  correction?: number,
): number | undefined => {
  if (temperature === undefined || correction === undefined) return undefined;
  const interpolatedCorrection = calculateInterpolatedCorrection(
    absenceTemperature,
    confortTemperature,
    temperature,
    correction,
  );
  return temperature - interpolatedCorrection;
};

/**
 * The function checks if a given hardware version is higher or equal to another hardware version.
 * @param {string} hardware - The `hardware` parameter is a string that represents the hardware version
 * you want to compare.
 * @param {string} hardwareToCompare - The `hardwareToCompare` parameter is a string that represents
 * the hardware version that you want to compare with.
 * @returns a boolean value.
 */
export const isValveHardwareIsUpper = (hardware?: string, hardwareToCompare?: VALVE_HARDWARE_VERSION_ENUM) => {
  if (!hardware || !hardwareToCompare) return false;
  const values = Object.values(VALVE_HARDWARE_VERSION_ENUM);
  return (
    values.indexOf(valveVersionHandler(hardware) as VALVE_HARDWARE_VERSION_ENUM) >= values.indexOf(hardwareToCompare)
  );
};

/**
 * Get the battery status of a valve.
 * @param stateData The state data of the valve.
 * @param permissions The permissions of the user.
 * @returns The battery status of the valve.
 */
export const getValveBatteryStatus = (
  stateData: NonNullable<Valve["stateData"]>,
  permissions: Permission,
  canSeeInstallerWarnings: boolean,
) => {
  const { batLowDate, batZ, batZUpdatedAt, dateBat } = stateData;
  const canReadChangedDate = permissions.valve?.stateData?.dateBat?.read;

  const level =
    batZ !== undefined && batZUpdatedAt
      ? m.from_to({ from: batZ, to: dayjs(batZUpdatedAt).format("LT - L") })
      : undefined;

  const hiddenStatus = level ? undefined : "-";

  return {
    status: batLowDate
      ? canSeeInstallerWarnings
        ? m.low_since({
            date: m.from_to({ from: dayjs(batLowDate).format("L"), to: dayjs(batLowDate).format("LT") }),
          })
        : hiddenStatus
      : batZ !== undefined
        ? m.battery_level({ level: getBatteryThreshold(batZ) })
        : "-",

    level,

    changed: canReadChangedDate && dateBat ? m.battery_changed_on({ date: dayjs(dateBat).format("L") }) : undefined,
  };
};
